import { memo } from 'react';
import type { computePassNetwork } from '@4cm/core/tactics/overlays';

/**
 * §17 — Pass-network overlay for the 2D tactics board.
 *
 * The Sky MNF / NFL "passing map" graphic: each same-team pair of
 * players gets a line whose thickness encodes connection strength.
 * Algorithm lives in `@4cm/core/tactics/overlays.computePassNetwork`;
 * this layer is pure rendering.
 *
 * Mapping convention matches `VoronoiLayer2D` — `PitchSvg` uses the
 * landscape swap
 *   SVG-x = -world.z
 *   SVG-y =  world.x
 * so edge endpoints emit `(-z, x)` for both `(from, to)`.
 *
 * The opacity prop bakes into the rgba stroke directly so the parent
 * can dial the network down without an extra `opacity` attribute on
 * every `<line>`.
 */
export type PassNetLayer2DEdges = ReturnType<typeof computePassNetwork>;

export interface PassNetLayer2DProps {
  edges: PassNetLayer2DEdges;
  /** Caller-controlled opacity in [0, 1]. */
  opacity: number;
}

// Home green / away amber — same family as `PlayerToken2D` and
// `VoronoiLayer2D`. Opacity baked into the rgba stroke.
function strokeFor(
  team: 'home' | 'away' | undefined,
  opacity: number,
): string {
  if (team === 'away') return `rgba(251, 191, 36, ${opacity})`;
  return `rgba(74, 222, 128, ${opacity})`;
}

// Map an edge weight in (0, 1] onto a visible stroke width in metres.
// `computePassNetwork` returns weight = 1 / (1 + distance_metres) so
// the realistic range on a tactics board is roughly (0.05, 0.5]. We
// clamp to keep the thinnest edges legible and the thickest from
// drowning the pitch markings. Bumped to match the bolder 3D layer
// so the connections feel one continuous network, not isolated wires.
const MIN_STROKE_W_M = 0.3;
const MAX_STROKE_W_M = 1.0;
function strokeWidthFor(weight: number): number {
  if (!Number.isFinite(weight) || weight <= 0) return MIN_STROKE_W_M;
  const w = MIN_STROKE_W_M + (MAX_STROKE_W_M - MIN_STROKE_W_M) * weight;
  if (w < MIN_STROKE_W_M) return MIN_STROKE_W_M;
  if (w > MAX_STROKE_W_M) return MAX_STROKE_W_M;
  return w;
}

/** Disc radius in world metres for the per-player node markers —
 *  same Sky-MNF / Next-Gen-Stats idiom the 3D layer uses. Slightly
 *  larger than the thickest connector so lines appear to flow INTO
 *  the node, not past it. */
const NODE_R_M = 1.1;

function PassNetLayer2DInner({ edges, opacity }: PassNetLayer2DProps) {
  if (!edges || edges.length === 0) return null;
  // De-dup endpoints by rounded position so two edges sharing a
  // player render one disc, not two stacked. Mirrors the 3D layer.
  const nodes = new Map<
    string,
    { x: number; z: number; team: 'home' | 'away' | 'neutral' }
  >();
  for (const edge of edges) {
    for (const p of [
      { p: edge.from, team: edge.team },
      { p: edge.to, team: edge.team },
    ]) {
      const k = `${Math.round(p.p.x * 10)},${Math.round(p.p.z * 10)}`;
      if (!nodes.has(k)) {
        nodes.set(k, {
          x: p.p.x,
          z: p.p.z,
          team: (p.team ?? 'neutral') as 'home' | 'away' | 'neutral',
        });
      }
    }
  }
  const nodeColorFor = (
    team: 'home' | 'away' | 'neutral',
    op: number,
  ): string => {
    if (team === 'away') return `rgba(251, 191, 36, ${op})`;
    return `rgba(74, 222, 128, ${op})`;
  };
  return (
    <g
      className="t2d-passnet-layer"
      data-testid="t2d-passnet-layer"
      pointerEvents="none"
    >
      {edges.map((edge, idx) => {
        // World (x, z) → SVG (-z, x) per the landscape mapping.
        const x1 = -edge.from.z;
        const y1 = edge.from.x;
        const x2 = -edge.to.z;
        const y2 = edge.to.x;
        return (
          <line
            key={`edge-${idx}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={strokeFor(edge.team, opacity)}
            strokeWidth={strokeWidthFor(edge.weight)}
            strokeLinecap="round"
            data-team={edge.team ?? 'neutral'}
            data-weight={edge.weight}
            data-testid={`t2d-passnet-edge-${idx}`}
          />
        );
      })}
      {/* Node discs at each unique player position — broadcast
          convention. Layered after the lines so the discs sit
          on top + the connectors flow into them visually. */}
      {Array.from(nodes.values()).map((n, idx) => (
        <circle
          key={`node-${idx}`}
          cx={-n.z}
          cy={n.x}
          r={NODE_R_M}
          fill={nodeColorFor(n.team, opacity)}
          data-team={n.team}
          data-testid={`t2d-passnet-node-${idx}`}
        />
      ))}
    </g>
  );
}

/** Memoised — edges are recomputed only when token positions change,
 *  so the parent typically hands the same array reference across
 *  renders driven by unrelated state (e.g. HUD chip toggles). */
export const PassNetLayer2D = memo(PassNetLayer2DInner);
